// ** React Imports
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
// ** Third Party Components
import ReactPaginate from "react-paginate"
import {
  ChevronDown,
  Edit,
  CheckCircle,
  HelpCircle,
  UserCheck,
  UserX,
  Archive,
  Flag,
  Eye,
  CheckSquare
} from "react-feather"
import DataTable from "react-data-table-component"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
// ** Custom Components
import Avatar from "@components/avatar"
// ** Reactstrap Imports
import { Input, Row, Col, Card, Badge, UncontrolledTooltip, Button } from "reactstrap"
// ** Store & Actions
import { useDispatch, useSelector } from "react-redux"
import { getData } from "@store/stores/to-validate"
// ** Styles
import "@styles/react/apps/app-invoice.scss"
import "@styles/react/libs/tables/react-dataTable-component.scss"
// ** Utils
import { getAllFromController, validatePerson } from "@lib/api"
import { dateISO, convertNameToColor } from "@lib/util"
import DeleteConfirmation from "../components/DeleteConfirmation"

const CustomHeader = ({
  searchValue,
  searchHandler,
  rowsPerPage,
  perPageChangeHandler,
  changeStatusHandler,
  statusValue,
  statusName
}) => {
  const [status, setStatus] = useState()

  useEffect(() => {
    getAllFromController("Entities/Status", "crm").then((res) => setStatus(res.data))
  }, [])

  // temp function solution to download csv
  const onDownloadHandler = () => {
    const url = `/api/Persons/Csv/${statusValue}`
    const win = window.open(url, "_blank")
    win.focus()
    win.addEventListener("unload", function () {
      win.close()
    })
  }

  return (
    <div className="invoice-list-table-header w-100 py-2">
      <Row>
        <Col lg="6" className="d-flex align-items-center px-0 px-lg-1">
          <div className="d-flex align-items-center me-2">
            <label htmlFor="rows-per-page">Show</label>
            <Input
              type="select"
              id="rows-per-page"
              value={rowsPerPage}
              onChange={perPageChangeHandler}
              className="form-control ms-50 pe-3"
            >
              <option value="20">20</option>
              <option value="35">35</option>
              <option value="50">50</option>
            </Input>
          </div>
        </Col>
        <Col
          lg="6"
          className="actions-right d-flex align-items-center justify-content-lg-end flex-lg-nowrap flex-wrap mt-lg-0 mt-1 pe-lg-1 p-0"
        >
          <div className="d-flex align-items-center">
            <label htmlFor="search-client">Search</label>
            <Input
              id="search-client"
              className="ms-50 me-2 w-100"
              type="text"
              defaultValue={searchValue}
              onChange={(e) => searchHandler(e.target.value)}
              placeholder="Name, Last Name"
            />
          </div>
          {status && (
            <div>
              <Input
                className="w-auto"
                type="select"
                onChange={changeStatusHandler}
                defaultValue={statusValue}
              >
                <option value={0}>All Status</option>
                {status.map((personStatus) => (
                  <option key={personStatus.id} value={personStatus.id}>
                    {personStatus.name}
                  </option>
                ))}
              </Input>
            </div>
          )}
          <Button color="light" size="sm" className="ms-50" onClick={onDownloadHandler}>
            Download '{statusName}'
          </Button>
        </Col>
      </Row>
    </div>
  )
}

const Visits = () => {
  const MySwal = withReactContent(Swal)
  // ** Store vars
  const dispatch = useDispatch()
  const store = useSelector((state) => state.toValidate)

  const [statusName, setStatusName] = useState("All Status")

  // ** Params for DataTable component and subsequent API calls
  const [dataTableParams, setDataTableParams] = useState({
    q: null,
    sort: "desc",
    sortColumn: "entityId",
    page: 1,
    perPage: 20,
    statusId: 0
  })

  // ** Init and update data each time params change
  useEffect(() => {
    dispatch(getData(dataTableParams))
  }, [dispatch, store.data.length, dataTableParams])

  // ** Functions to handle filter changes
  const searchHandler = (val) => {
    const newdataTableParams = { ...dataTableParams, q: val, page: 1 }
    setDataTableParams(newdataTableParams)
  }
  const perPageChangeHandler = (e) => {
    const newdataTableParams = { ...dataTableParams, perPage: parseInt(e.target.value), page: 1 }
    setDataTableParams(newdataTableParams)
  }
  const changeStatusHandler = (e) => {
    const statusId = parseInt(e.target.value)
    setStatusName(e.target.selectedOptions[0].text)
    const newdataTableParams = { ...dataTableParams, statusId, page: 1 }
    setDataTableParams(newdataTableParams)
  }

  const handlePagination = (page) => {
    const newdataTableParams = { ...dataTableParams, page: page.selected + 1 }
    setDataTableParams(newdataTableParams)
  }
  const handleSort = (column, sortDirection) => {
    const newdataTableParams = {
      ...dataTableParams,
      sort: sortDirection,
      sortColumn: column.sortField
    }
    setDataTableParams(newdataTableParams)
  }

  // ** DataTable columns
  // ------------------------------
  const PERSON_STATUS = {
    1: { color: "light-primary", icon: Flag },
    10: { color: "light-success", icon: UserCheck },
    20: { color: "light-warning", icon: UserX },
    99: { color: "light-dark", icon: Archive }
  }

  // ** Handlers
  const validateConfirmation = (personId) => {
    MySwal.fire({
      title: "Confirm ?",
      html: (
        <p>
          Are you sure you want to <b>validate</b> this person?
        </p>
      ),
      customClass: {
        input: "mx-3",
        confirmButton: "btn btn-success mx-1",
        cancelButton: "btn btn-danger ms-1"
      },
      buttonsStyling: false,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      async preConfirm() {
        return await validatePerson(personId)
      }
    }).then(function (res) {
      if (res.isConfirmed) {
        if (res.value.status === 204) {
          MySwal.fire({
            position: "bottom-end",
            icon: "success",
            title: "Validated",
            showConfirmButton: false,
            timer: 1200,
            backdrop: false
          })
          dispatch(getData(dataTableParams))
        } else {
          MySwal.fire({
            title: "Error",
            html: (
              <div>
                <p>An error occured. Please verify this person's data before validating it.</p>
                <small>Person ID : {personId}</small>
              </div>
            ),
            icon: "error",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              confirmButton: "btn btn-primary"
            }
          })
        }
      }
    })
  }

  // ** Content
  const columns = [
    {
      sortable: true,
      minWidth: "85px",
      maxWidth: "85px",
      sortField: "statusId",
      name: " ",
      cell: (row) => {
        const color = PERSON_STATUS[row.status.id] ? PERSON_STATUS[row.status.id].color : "primary",
          Icon = PERSON_STATUS[row.status.id] ? PERSON_STATUS[row.status.id].icon : HelpCircle
        return (
          <>
            <Avatar color={color} icon={<Icon size={14} />} id={`av-tooltip-${row.id}`} />
            <UncontrolledTooltip placement="top" target={`av-tooltip-${row.id}`}>
              <span className="fw-bold">{row.status.name}</span>
              <br />
              <span>Created:</span> {dateISO(row.createdOn, true, true)}
              <br />
              <span>Last update:</span> {dateISO(row.updatedOn, true, true)}
            </UncontrolledTooltip>
          </>
        )
      }
    },
    {
      name: "Client",
      sortable: true,
      minWidth: "320px",
      sortField: "name",
      cell: (row) => {
        const name = row.name ? row.name : "",
          firstName = row.firstName ? row.firstName : "",
          toValidate = row.status.id === 1
        return (
          <Link
            to={`/CRM/Visits/${row.id}${toValidate ? "/Validate" : ""}`}
            className="d-flex justify-content-left align-items-center"
          >
            <Avatar
              color={`light-${convertNameToColor(row.name[0])}`}
              className="me-50"
              content={`${name} ${firstName}`}
              initials
            />
            <div className="d-flex flex-column">
              <h6 className="user-name mb-0 fw-bolder">
                {name.toUpperCase()} {firstName}
              </h6>
              {(row.emails.length > 0 || row.phones.length > 0) && (
                <div className="text-body">
                  {row.emails.length > 0 && row.emails[0].address}
                  {row.emails.length > 0 && row.phones.length > 0 && " - "}
                  {row.phones.length > 0 && row.phones[0].number}
                </div>
              )}
            </div>
          </Link>
        )
      }
    },
    {
      name: "Main Address",
      sortable: false,
      minWidth: "200px",
      maxWidth: "470px",
      cell: (row) => {
        if (row.addresses.length > 0) {
          return (
            <div className="d-flex align-items-center">
              <span>
                {row.addresses[0].street} {row.addresses[0].additional}
                <br />
                {row.addresses[0].postCode} {row.addresses[0].city?.name}
              </span>
              {row.addresses.length > 1 && (
                <div className="ms-3">
                  <Badge color="light-secondary" pill id={`additionalAddress-${row.id}`}>
                    + {row.addresses.length - 1}
                  </Badge>
                  <UncontrolledTooltip placement="top" target={`additionalAddress-${row.id}`}>
                    {row.addresses.length - 1} additional address
                  </UncontrolledTooltip>
                </div>
              )}
            </div>
          )
        } else {
          return null
        }
      }
    },
    {
      sortable: true,
      maxWidth: "150px",
      name: "Birth Date",
      sortField: "birthDate",
      cell: (row) => {
        const estimated = row.isBirthDateExact ? <CheckSquare size={13} /> : "~"
        return (
          <span>
            {estimated} {dateISO(row.birthDate, true, false)}
          </span>
        )
      }
    },
    {
      sortable: false,
      name: "Categories",
      minWidth: "100px",
      maxWidth: "250px",
      cell: (row) => {
        if (row.categories.length !== 0) {
          return row.categories.map((category) => {
            return (
              <Badge key={category.id} color="light-primary" pill>
                {category.name}
              </Badge>
            )
          })
        } else {
          return null
        }
      }
    },
    {
      name: "Actions",
      right: true,
      maxWidth: "120px",
      cell: (row) => {
        const toValidate = row.status.id === 1
        const Icon = toValidate ? Eye : Edit
        return (
          <div className="d-flex">
            {toValidate && (
              <Button
                color="flat-success"
                size="sm"
                id={`validate-${row.id}`}
                onClick={() => validateConfirmation(row.id)}
              >
                <CheckCircle size={17} color="green" />
                <UncontrolledTooltip placement="left" target={`validate-${row.id}`}>
                  Validate
                </UncontrolledTooltip>
              </Button>
            )}
            <Link
              tag="a"
              to={`/CRM/Visits/${row.id}${toValidate ? "/Validate" : ""}`}
              id={`edit-tooltip-${row.id}`}
            >
              <Button color="flat-dark" size="sm">
                <Icon size={17} color="black" />
                <UncontrolledTooltip placement="top" target={`edit-tooltip-${row.id}`}>
                  {toValidate ? "Preview and validate" : "Edit"}
                </UncontrolledTooltip>
              </Button>
            </Link>
            <DeleteConfirmation
              id={row.id}
              endpoint="Persons"
              onSuccess={() => dispatch(getData(dataTableParams))}
            />
          </div>
        )
      }
    }
  ]

  // ** Pagination
  const CustomPagination = () => {
    const count = parseInt(Math.max(1, Math.ceil(store.total / dataTableParams.perPage))) || 1
    return (
      <ReactPaginate
        nextLabel=""
        breakLabel="..."
        previousLabel=""
        pageCount={count}
        activeClassName="active"
        breakClassName="page-item"
        pageClassName={"page-item"}
        breakLinkClassName="page-link"
        nextLinkClassName={"page-link"}
        pageLinkClassName={"page-link"}
        nextClassName={"page-item next"}
        previousLinkClassName={"page-link"}
        previousClassName={"page-item prev"}
        onPageChange={(page) => handlePagination(page)}
        forcePage={dataTableParams.page !== 0 ? dataTableParams.page - 1 : 0}
        containerClassName={"pagination react-paginate justify-content-center p-5"}
      />
    )
  }

  return (
    <div className="invoice-list-wrapper">
      <Card>
        <div className="invoice-list-dataTable react-dataTable">
          <DataTable
            noHeader
            pagination
            sortServer
            paginationServer
            subHeader={true}
            columns={columns}
            responsive={true}
            onSort={handleSort}
            defaultSortFieldId={1}
            defaultSortAsc={false}
            data={store.data.length > 0 ? store.data : []}
            sortIcon={<ChevronDown />}
            className="react-dataTable"
            paginationDefaultPage={dataTableParams.page}
            paginationComponent={CustomPagination}
            subHeaderComponent={
              <CustomHeader
                searchValue={dataTableParams.q}
                searchHandler={searchHandler}
                rowsPerPage={dataTableParams.perPage}
                perPageChangeHandler={perPageChangeHandler}
                changeStatusHandler={changeStatusHandler}
                statusValue={dataTableParams.statusId}
                statusName={statusName}
              />
            }
          />
        </div>
      </Card>
    </div>
  )
}

export default Visits
